import type { LeadListQuery, LeadListResponse, LeadRow, TableColumn } from "@gioia365/contracts";
import { keepPreviousData, useQueries, useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { TabContext } from "../session/tab-context";
import { truncateText } from "../text";
import type { TableLayoutState } from "./columns/layout-queries";
import { columnWidths, companyFieldKeys, listIncludes, moveVisibleColumn, setColumnVisible, setColumnWidth, visibleColumnDefs } from "./columns/table-layout";
import { withoutControlCharacters } from "./lead-display";
import { LEAD_PAGE_SIZE, leadListQuery } from "./lead-queries";

// Le parti comuni delle pagine con la tabella dei Lead: l'elenco «Lead» (piano M2 Task 17, M3 Task 4 e 6) e la pagina di
// una Smart List (M3 Task 9, decisione P30). Le righe a pagine lette quando entrano nella finestra, la ricerca nei
// risultati che scrive l'indirizzo dopo l'ultimo tasto, e le colonne della vista in uso con i loro cambi.

/** Il limite della ricerca (`leadListQuerySchema`): oltre, il server risponderebbe 422. */
export const SEARCH_MAX_LENGTH = 200;

/**
 * Una ricerca dell'indirizzo (`q` e `cercaArchiviati` dell'elenco dei Lead, `q` di «Conversazioni»): un testo senza caratteri
 * di controllo e non più lungo di quanto il server accetta, oppure niente. Il router legge un numero scritto nell'indirizzo
 * come numero: una ricerca «300» torna testo.
 */
export function searchParamText(value: unknown): string | undefined {
  const raw = typeof value === "number" ? String(value) : value;
  if (typeof raw !== "string") return undefined;
  // Accorciata senza spezzare un'emoji: la metà rimasta farebbe rispondere 422 al server.
  const text = truncateText(withoutControlCharacters(raw), SEARCH_MAX_LENGTH);
  return text.trim() ? text : undefined;
}

/** Le righe vuote che tengono il posto della prima pagina mentre arriva: la struttura non salta. */
export const LOADING_ROWS = 8;
/** Quanto si aspetta dopo l'ultimo tasto prima di cercare: niente richieste a ogni lettera. */
const SEARCH_DELAY_MS = 200;

/** Le pagine dalla prima all'ultima. */
function pagesBetween(firstPage: number, lastPage: number): number[] {
  const pages: number[] = [];
  for (let page = firstPage; page <= lastPage; page += 1) pages.push(page);
  return pages;
}

/**
 * Una pagina arrivata per la vista di adesso: il suo numero (da 0), le sue righe e quando il server le ha date
 * (`dataUpdatedAt`): una pagina riletta ha un istante più recente.
 */
export type LoadedPage = { page: number; rows: readonly LeadRow[]; updatedAt: number };

/**
 * Le righe di una vista (`base`, la lettura senza la pagina): la prima pagina porta il totale, e mentre arriva quella di
 * una vista nuova resta in vista la precedente; le altre si leggono quando le loro righe entrano nella finestra (piano
 * M2 D5). Si ricordano soltanto le pagine visibili, non le righe: scorrere dentro la stessa pagina non ridisegna nulla.
 * `loadedPages` sono le pagine di questa vista che ci sono adesso (la prima e quelle nella finestra), mai quelle della
 * vista di prima mostrate mentre arriva la nuova: le caselle delle azioni multiple (piano M3 Task 14) si confrontano con
 * queste. `key` identifica la lettura (ricerca, filtri, ordinamento, colonne).
 */
export function useLeadPages(input: { tab: TabContext; base: Omit<LeadListQuery, "offset">; view: string | null }) {
  const { tab, base, view } = input;
  const baseKey = JSON.stringify(base);
  const first = useQuery({ ...leadListQuery(tab, { ...base, offset: 0 }, view), placeholderData: keepPreviousData });
  const total = first.data?.total ?? 0;

  const [range, setRange] = useState<{ key: string; firstPage: number; lastPage: number }>({ key: baseKey, firstPage: 0, lastPage: 0 });
  const handleRange = useCallback(
    (firstRow: number, lastRow: number) => {
      const firstPage = Math.floor(firstRow / LEAD_PAGE_SIZE);
      const lastPage = Math.floor(lastRow / LEAD_PAGE_SIZE);
      setRange((current) =>
        current.key === baseKey && current.firstPage === firstPage && current.lastPage === lastPage ? current : { key: baseKey, firstPage, lastPage },
      );
    },
    [baseKey],
  );
  const visiblePages = range.key === baseKey ? pagesBetween(range.firstPage, range.lastPage) : [0];
  const otherPages = visiblePages.filter((page) => page > 0 && page * LEAD_PAGE_SIZE < total && !first.isPlaceholderData);
  const others = useQueries({ queries: otherPages.map((page) => leadListQuery(tab, { ...base, offset: page * LEAD_PAGE_SIZE }, view)) });

  const pageData = new Map<number, LeadListResponse>();
  const loadedPages: LoadedPage[] = [];
  if (first.data) pageData.set(0, first.data);
  if (first.data && !first.isPlaceholderData) loadedPages.push({ page: 0, rows: first.data.rows, updatedAt: first.dataUpdatedAt });
  otherPages.forEach((page, index) => {
    const result = others[index];
    const data = result?.data;
    if (!data) return;
    pageData.set(page, data);
    loadedPages.push({ page, rows: data.rows, updatedAt: result.dataUpdatedAt });
  });
  const rowAt = (index: number): LeadRow | undefined => {
    const page = Math.floor(index / LEAD_PAGE_SIZE);
    return pageData.get(page)?.rows[index - page * LEAD_PAGE_SIZE];
  };
  const failedPages = others.filter((result) => result.isError);

  return { first, total, rowAt, handleRange, failedPages, loadedPages, key: baseKey };
}

/**
 * Il testo della ricerca nei risultati: `write` porta la ricerca nell'indirizzo 200 ms dopo l'ultimo tasto (`write` deve
 * restare la stessa funzione fra un disegno e l'altro); un indirizzo cambiato da fuori («Indietro», «Azzera la ricerca»)
 * riporta il testo nella casella.
 */
export function useResultSearch(q: string | undefined, write: (q: string | undefined) => void) {
  const [text, setText] = useState(q ?? "");
  const [syncedQ, setSyncedQ] = useState(q);
  if (q !== syncedQ) {
    setSyncedQ(q);
    if ((q ?? "").trim() !== text.trim()) setText(q ?? "");
  }
  useEffect(() => {
    const next = text.trim() ? text : undefined;
    if ((next ?? "").trim() === (q ?? "").trim()) return;
    const timer = setTimeout(() => write(next), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [text, q, write]);
  return { text, setText };
}

/**
 * Le colonne della vista in uso (D5): quelle visibili con le loro larghezze, i campi dell'azienda e le colonne di sistema da
 * chiedere con le righe (`fields=` e `include=`), e i cambi dalla tabella (sposta, allarga, nascondi), che restano sul
 * dispositivo finché non si salvano.
 */
export function useColumnArrange(layout: TableLayoutState) {
  const layoutColumns = layout.resolved.columns;
  const tableColumns = useMemo(() => visibleColumnDefs(layout.catalog, layoutColumns), [layout.catalog, layoutColumns]);
  const widths = useMemo(() => columnWidths(layoutColumns), [layoutColumns]);
  const fields = companyFieldKeys(layoutColumns).join(",");
  const include = listIncludes(layoutColumns);
  const changeLayout = layout.change;
  const arrange = useCallback((next: (columns: TableColumn[]) => TableColumn[]) => changeLayout(next(layoutColumns)), [changeLayout, layoutColumns]);
  const moveColumn = useCallback((fieldId: string, toIndex: number) => arrange((columns) => moveVisibleColumn(columns, fieldId, toIndex)), [arrange]);
  const resizeColumn = useCallback((fieldId: string, width: number) => arrange((columns) => setColumnWidth(columns, fieldId, width)), [arrange]);
  const hideColumn = useCallback((fieldId: string) => arrange((columns) => setColumnVisible(columns, fieldId, false)), [arrange]);
  return { tableColumns, widths, fields, include, moveColumn, resizeColumn, hideColumn };
}
